// src/pages/gt3x/Streamer.jsx
import { useEffect, useMemo, useState } from "react";
import PageHero from "../../components/PageHero.jsx";
import { fetchSheetViaProxy } from "../../lib/sheets";

const RANGE = "Streamers!A1:C500";

const TWITCH_ICON = "https://racevision.info/assets/twitch.png";
const YOUTUBE_ICON = "https://racevision.info/assets/youtube.png";

function normalizeUrl(value) {
    const str = String(value ?? "").trim();
    if (!str) return "";

    if (/^https?:\/\//i.test(str)) return str;
    return `https://${str.replace(/^https?:\/\//i, "")}`;
}

function getChannelNameFromUrl(url) {
    const href = normalizeUrl(url);
    if (!href) return "";

    try {
        const u = new URL(href);
        const parts = u.pathname.split("/").filter(Boolean);

        if (!parts.length) return u.hostname.replace(/^www\./i, "");

        return parts[parts.length - 1];
    } catch {
        const clean = href.split("?")[0].replace(/\/+$/, "");
        const parts = clean.split("/").filter(Boolean);
        return parts.length ? parts[parts.length - 1] : clean;
    }
}

function getPlatform(url) {
    const href = normalizeUrl(url).toLowerCase();

    if (href.includes("twitch.tv")) return "twitch";
    if (href.includes("youtube.com") || href.includes("youtu.be")) return "youtube";

    return "";
}

function platformLabel(platform) {
    if (platform === "twitch") return "Twitch";
    if (platform === "youtube") return "YouTube";
    return "Stream";
}

function platformIcon(platform) {
    if (platform === "twitch") return TWITCH_ICON;
    if (platform === "youtube") return YOUTUBE_ICON;
    return "";
}

function renderChannelLink(value) {
    const href = normalizeUrl(value);
    if (!href) return null;

    const platform = getPlatform(href);
    const channelName = getChannelNameFromUrl(href) || href;
    const iconSrc = platformIcon(platform);

    return (
        <a
            href={href}
            target="_blank"
            rel="noreferrer"
            title={href}
            className="button button--secondary"
            style={{
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                width: "fit-content",
            }}
        >
            {iconSrc ? (
                <img
                    src={iconSrc}
                    alt={platformLabel(platform)}
                    style={{
                        height: platform === "twitch" ? 24 : 22,
                        width: platform === "twitch" ? 24 : 22,
                        objectFit: "contain",
                        display: "block",
                        flexShrink: 0,
                    }}
                />
            ) : null}

            <span>{channelName}</span>
        </a>
    );
}

function StreamerCard({ fahrer, link, team }) {
    const href = normalizeUrl(link);
    const platform = getPlatform(href);
    const channelName = getChannelNameFromUrl(href) || "Channel";
    const iconSrc = platformIcon(platform);

    return (
        <article className="rv-modern-card rv-modern-card--accent">
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "flex-start",
                    marginBottom: 12,
                }}
            >
                <div>
                    <div className="rv-section-kicker">
                        Live Channel
                    </div>

                    <h3
                        style={{
                            fontSize: 24,
                            marginBottom: 4,
                        }}
                    >
                        {fahrer || "Unbekannter Fahrer"}
                    </h3>

                    <p className="rv-modern-muted" style={{ margin: 0 }}>
                        {team || "Kein Team hinterlegt"}
                    </p>
                </div>

                {iconSrc ? (
                    <div
                        style={{
                            width: 44,
                            height: 44,
                            borderRadius: 14,
                            display: "grid",
                            placeItems: "center",
                            background: "rgba(255,255,255,.055)",
                            border: "1px solid rgba(255,255,255,.10)",
                        }}
                    >
                        <img
                            src={iconSrc}
                            alt=""
                            aria-hidden="true"
                            style={{
                                width: 28,
                                height: 28,
                                objectFit: "contain",
                            }}
                        />
                    </div>
                ) : (
                    <span className="rv-status-pill rv-status-pill--info">
                        Live
                    </span>
                )}
            </div>

            <div className="rv-info-grid" style={{ marginBottom: 14 }}>
                <div className="rv-info-tile">
                    <div className="rv-info-tile__label">Channel</div>
                    <div className="rv-info-tile__value">{channelName}</div>
                </div>
            </div>

            {renderChannelLink(link)}
        </article>
    );
}

export default function Streamer() {
    const [data, setData] = useState({ header: [], rows: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;

        async function load() {
            try {
                setLoading(true);
                setError("");

                const res = await fetchSheetViaProxy({ range: RANGE });

                if (!cancelled) {
                    setData(res);
                }
            } catch (e) {
                if (!cancelled) {
                    setError(e?.message || "Fehler beim Laden der Streamer-Daten.");
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        load();

        return () => {
            cancelled = true;
        };
    }, []);

    const rows = useMemo(() => {
        const headers = data.header || [];
        const rowsObj = data.rows || [];

        const arr = rowsObj.map((r) => [
            r[headers[0]],
            r[headers[1]],
            r[headers[2]],
        ]);

        return arr.filter(([fahrer, link, team]) => {
            const f = String(fahrer ?? "").trim();
            const l = String(link ?? "").trim();
            const t = String(team ?? "").trim();

            if (!f && !l && !t) return false;

            if (
                f.toLowerCase() === "fahrer" &&
                l.toLowerCase() === "link"
            ) {
                return false;
            }

            return true;
        });
    }, [data]);

    const stats = useMemo(() => {
        const twitch = rows.filter(([, link]) => getPlatform(link) === "twitch").length;
        const youtube = rows.filter(([, link]) => getPlatform(link) === "youtube").length;

        return {
            total: rows.length,
            twitch,
            youtube,
        };
    }, [rows]);

    return (
        <div className="rv-page">
            <PageHero
                kicker="GT3 Xperience"
                title="Streamer"
            >
                Hier findest du Stream-Links von Fahrern und Teams der RaceVision GT3 Xperience.
                Perfekt, um Trainings, Rennen oder Onboards live mitzuverfolgen.
            </PageHero>

            <section className="rv-modern-card">
                <div className="rv-section-head">
                    <div>
                        <div className="rv-section-kicker">Broadcast</div>
                        <h2>Live Channels</h2>
                    </div>

                    <div className="rv-pill-row">
                        <span className="rv-status-pill rv-status-pill--info">
                            Gesamt: {stats.total}
                        </span>
                        <span className="rv-status-pill rv-status-pill--warn">
                            Twitch: {stats.twitch}
                        </span>
                        <span className="rv-status-pill rv-status-pill--ok">
                            YouTube: {stats.youtube}
                        </span>
                    </div>
                </div>

                {loading && (
                    <p className="rv-modern-muted">
                        Lade Streamer-Liste …
                    </p>
                )}

                {!loading && error && (
                    <p style={{ color: "var(--err)" }}>
                        {error}
                    </p>
                )}

                {!loading && !error && rows.length === 0 && (
                    <div
                        style={{
                            textAlign: "center",
                            padding: 28,
                            borderRadius: 18,
                            background: "rgba(255,255,255,.035)",
                            border: "1px solid rgba(255,255,255,.08)",
                        }}
                    >
                        <h2>Noch keine Streamer eingetragen</h2>
                        <p className="rv-modern-muted">
                            Sobald Stream-Links im Sheet hinterlegt sind, erscheinen sie hier.
                        </p>
                    </div>
                )}

                {!loading && !error && rows.length > 0 && (
                    <div className="rv-modern-grid">
                        {rows.map(([fahrer, link, team], idx) => (
                            <StreamerCard
                                key={`${fahrer}-${link}-${idx}`}
                                fahrer={fahrer}
                                link={link}
                                team={team}
                            />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}